import { Component } from 'react';
import { connect } from 'react-redux';

import Icon from 'components/atoms/Icon';
import LoaderSpinner from 'components/atoms/LoaderSpinner';
import { bindActionCreators, Dispatch } from 'redux';
import { setOutlineIntervalAction } from 'store/actions/mapActions';
import { twMerge } from 'tailwind-merge';
import { DataDisplayType, OpenModal, PortType, PositionType, RootState, ToolTipType, TrackType } from 'types';
import { MainControlsInterface, MapControlsInterface, MenuControlsInterface } from 'types/functions';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { checkDifferent, clone } from 'utils/formHandlers';
import { formatEventText } from 'utils/migrations';
import { translate } from 'utils/translate';

interface EventToolTipProps {
    //unused props
    outlineInterval: number;
    toolTip: ToolTipType;

    //unused props
    mainControls: MainControlsInterface;

    menuControls: MenuControlsInterface;
    mapControls: MapControlsInterface;
    currentTime: DateTimeFormat | null;
    port: PortType;

    //unused props
    dateTimeFormat: DateTimeFormat;
    mode: string;
    //unused props
    openModelFunction: OpenModal;

    //unused props
    setOutlineIntervalAction: (outlineInterval: number) => void;
}

interface EventToolTipState {
    toolTip: ToolTipType | null;
    track: TrackType | null;
    dataDisplay: DataDisplayType;
    position: PositionType | null;
    loading: boolean;
    analysisVariables: any;
}

class EventToolTip extends Component<EventToolTipProps, EventToolTipState> {
    constructor(props: EventToolTipProps) {
        super(props);
        this.state = {
            dataDisplay: props.port.meta_data.dataDisplay,
            toolTip: clone(props.toolTip),
            position: null,
            track: null,
            loading: true,
            analysisVariables: props.port.meta_data.analysisVariables,
        };
    }

    static getDerivedStateFromProps(props: EventToolTipProps, state: EventToolTipState) {
        if (checkDifferent(props.toolTip, state.toolTip)) {
            return {
                toolTip: clone(props.toolTip),
                track: null,
                position: null,
                loading: true,
            };
        }
        return null;
    }

    componentDidMount(): void {
        this.setState(
            {
                toolTip: this.props.toolTip,
            },
            this.loadTrackData
        );
    }

    componentDidUpdate(prevProps: EventToolTipProps, prevState: EventToolTipState) {
        if (checkDifferent(prevState.toolTip, this.state.toolTip)) {
            this.loadTrackData();
        }
    }

    loadTrackData = () => {
        this.setState({
            loading: false,
            //@ts-ignore
            track: this.props.menuControls.playback.getTrack(this.state.toolTip?.data.parent),
            //@ts-ignore
            position: this.props.menuControls.playback.getBestPosition(this.state.toolTip?.data.parent, null, {
                //@ts-ignore
                east_utm: this.state.toolTip?.east_utm,
                //@ts-ignore
                north_utm: this.state.toolTip?.north_utm,
            }),
        });
    };

    getDisplay = (type: string, key: string) => {
        const dataDisplay = this.state?.dataDisplay;

        if (dataDisplay?.[type]?.[key]) {
            return dataDisplay[type][key];
        }

        return {};
    };

    displayValue = (value) => {
        if (!this.state.position) {
            return '-';
        }

        if (this.state.position[value] === 'N/A') {
            return 'N/A';
        }

        return `${parseFloat(
            //@ts-ignore
            this.state.position[value] * this.getDisplay('transit', value).database2display_unit_factor
        ).toFixed(this.getDisplay('transit', value).decimal)}${this.getDisplay('transit', value).unit}`;
    };

    render() {
        return (
            <div className="flex flex-col p-2 min-w-40 rounded-md">
                <div className="w-full flex flex-col">
                    <div className="flex justify-between gap-x-2 items-start">
                        <div className="capitalize ">
                            {this.state.toolTip?.data.name} ( {this.state.track?.vessel.vessel_category.name})
                        </div>
                        <div
                            onClick={() => {
                                this.props.mapControls.chart?.hideTooltipById(this.props.toolTip.uuid);
                            }}
                            className="flex justify-center items-end justify-items-end cursor-pointer"
                        >
                            <Icon className="w-4 h-4">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <path d="M18 6l-12 12"></path>
                                <path d="M6 6l12 12"></path>
                            </Icon>
                        </div>
                    </div>

                    {this.state.loading ? (
                        <LoaderSpinner className="my-10 mx-20" variant="button"></LoaderSpinner>
                    ) : (
                        <>
                            <div className="flex-1 w-full truncate">
                                <div className="flex justify-between items-start">
                                    <div className="  items-center   ">
                                        <h3 className="text-activeGray text-sm leading-5 font-medium truncate">
                                            {this.state.track?.vessel.loa_m}
                                            {this.getDisplay('vessel', 'loa_m').unit} x{' '}
                                            {this.state.track?.vessel.beam_m}
                                            {this.getDisplay('vessel', 'beam_m').unit} (
                                            {this.state.track?.vessel.draft_m}
                                            {this.getDisplay('vessel', 'draft_m').unit})
                                        </h3>
                                        <p
                                            className={twMerge(
                                                this.state.position?.valid_time_utc &&
                                                    this.props?.currentTime &&
                                                    this.state.position?.valid_time_utc?.getDiffInSeconds(
                                                        this.props?.currentTime
                                                    ) <
                                                        (this.state.toolTip?.dataIndex?.navigational_status_id === 5 ||
                                                        this.state.toolTip?.dataIndex?.navigational_status_id === 1
                                                            ? this.state.analysisVariables?.aisDataDropoutMoored_seconds
                                                            : this.state.analysisVariables
                                                                  ?.aisDataDropoutUnderway_seconds)
                                                    ? ' text-red-500'
                                                    : ' text-gray-500',
                                                'mb-1',
                                                'text-sm',
                                                'font-bold',
                                                'leading-4',
                                                'truncate'
                                            )}
                                        >
                                            <span>
                                                {this.state.position?.valid_time_utc.formatDateTimeToString('dd LLL yyyy')}
                                            </span>
                                            <span className="ml-1">
                                                {this.state.position?.valid_time_utc.formatDateTimeToString(
                                                    'HH:mm:ss Z'
                                                )}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <div className="h-px w-full bg-gray-300 my-1"></div>
                            </div>
                            <div className="flex-1 w-full truncate">
                                <div className="flex justify-between items-start">
                                    <div className="  items-center   ">
                                        <p
                                            className={twMerge(
                                                this.state.toolTip?.dataIndex.group,
                                                'mb-1',
                                                'text-sm',
                                                'font-bold',
                                                'leading-4',
                                                'truncate'
                                            )}
                                        >
                                            <span>
                                                {translate(this.state.toolTip?.dataIndex.event)}{' '}
                                                {translate(formatEventText(this.state.toolTip?.dataIndex.description))}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <div className="h-px w-full bg-gray-300 my-1"></div>
                            </div>
                            <div className="py-1  text-sm  ">
                                <div className="flex justify-between items-center  ">
                                    <div>
                                        <div className="flex justify-start items-center gap-2 mr-2 ">
                                            <div className="font-bold">
                                                <span className="text-gray-600">
                                                    {this.getDisplay('transit', 'heading_deg').name} :
                                                </span>
                                                <span className="text-black">{this.displayValue('heading_deg')}</span>
                                            </div>
                                        </div>
                                        <div className="flex justify-start items-center gap-2 mr-2 ">
                                            <div className="font-bold">
                                                <span className="text-gray-600">
                                                    {this.getDisplay('transit', 'cog_deg').name} :
                                                </span>
                                                <span className="text-black">{this.displayValue('cog_deg')}</span>
                                            </div>
                                        </div>
                                        <div className="flex justify-start items-center gap-2 mr-2 ">
                                            <div className="font-bold">
                                                <span className="text-gray-600">
                                                    {this.getDisplay('transit', 'drift_angle_deg').name} :
                                                </span>
                                                <span className="text-black">
                                                    {this.displayValue('drift_angle_deg')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex justify-start items-center gap-2 ">
                                            <div className="font-bold">
                                                <span className="text-gray-600">
                                                    {this.getDisplay('transit', 'sog_mps').name} :
                                                </span>
                                                <span className="text-black">{this.displayValue('sog_mps')}</span>
                                            </div>
                                        </div>
                                        <div className="flex justify-start items-center gap-2 ">
                                            <div className="font-bold">
                                                <span className="text-gray-600">
                                                    {this.getDisplay('transit', 'rot_deg_min').name} :
                                                </span>
                                                <span className="text-black">{this.displayValue('rot_deg_min')}</span>
                                            </div>
                                        </div>
                                        <div className="flex justify-start items-center gap-2 ">
                                            <div className="font-bold">
                                                <span className="text-gray-600">
                                                    {this.getDisplay('transit', 'crot_deg_min').name} :
                                                </span>
                                                <span className="text-black">{this.displayValue('crot_deg_min')}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

const eventToolTipStateToProps = (state: RootState) => ({
    outlineInterval: state.map.outlineInterval,
    port: state.port.port,
    dateTimeFormat: state.port.dateTimeFormat,
    mainControls: state.layout.mainControls,
    mode: state.layout.mode,
    menuControls: state.layout.menuControls,
    mapControls: state.map.mapControls,
    openModelFunction: state.layout.openModelFunction,
});

const eventToolTipDispatchToProps = (dispatch: Dispatch) => ({
    setOutlineIntervalAction: bindActionCreators(setOutlineIntervalAction, dispatch),
});

export default connect(eventToolTipStateToProps, eventToolTipDispatchToProps)(EventToolTip);
